import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import type { CreateRateItemInput, RateCardWithItems } from "./rateCards.types";
import RateItemsReviewTable from "./RateItemsReviewTable";
import {
	createEmptyRateItem,
	hasRateItemChanged,
	isValidRateItem,
	rateItemToInput,
} from "./rateCardItems.utils";
import { createRateItem, deleteRateItem, getRateCard, updateRateItem } from "./rateCardsAPI";

interface RateCardDetailModalProps {
	organizationId: string;
	rateCardId: string;
	onClose: () => void;
	/** Called after a successful save so the parent can refresh its list. */
	onSaved?: () => void;
}

function formatDate(value: number): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function RateCardDetailModal({ organizationId, rateCardId, onClose, onSaved }: RateCardDetailModalProps) {
	const [card, setCard] = useState<RateCardWithItems | null>(null);
	const [items, setItems] = useState<CreateRateItemInput[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	const loadCard = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getRateCard(organizationId, rateCardId);
			setCard(data);
			setItems((data.items ?? []).map(rateItemToInput));
		} catch (error) {
			console.error("Error loading rate card:", error);
			toast.error("Failed to load rate card");
		} finally {
			setIsLoading(false);
		}
	}, [organizationId, rateCardId]);

	useEffect(() => {
		void loadCard();
	}, [loadCard]);

	const handleItemChange = (index: number, item: CreateRateItemInput) => {
		setItems((current) => current.map((existing, i) => (i === index ? item : existing)));
	};

	const handleItemRemove = (index: number) => {
		setItems((current) => current.filter((_, i) => i !== index));
	};

	const handleAddRow = () => {
		setItems((current) => [...current, createEmptyRateItem()]);
	};

	const allItemsValid = useMemo(() => items.every(isValidRateItem), [items]);
	const canSave = !isSaving && !isLoading && allItemsValid;

	const handleSave = async () => {
		if (!canSave || !card) return;
		setIsSaving(true);
		try {
			const originalById = new Map(card.items.map((item) => [item.id, item]));
			const draftIds = new Set(items.map((item) => item.id).filter((id): id is string => Boolean(id)));

			const creates = items.filter((item) => !item.id);
			const updates = items.filter((item) => {
				if (!item.id) return false;
				const original = originalById.get(item.id);
				return original ? hasRateItemChanged(item, original) : false;
			});
			const deletes = card.items.filter((item) => !draftIds.has(item.id));

			const changeCount = creates.length + updates.length + deletes.length;
			if (changeCount === 0) {
				toast.success("No changes to save");
				return;
			}

			// allSettled (not all) so one failure doesn't abandon the rest of the batch mid-flight,
			// which would otherwise leave the server partially updated while local state stays stale.
			const results = await Promise.allSettled([
				...creates.map((item) => createRateItem(organizationId, rateCardId, item)),
				...updates.map((item) =>
					updateRateItem(organizationId, rateCardId, item.id!, {
						unit: item.unit,
						unitPrice: item.unitPrice,
						lineItemTemplate: item.lineItemTemplate,
					}),
				),
				...deletes.map((item) => deleteRateItem(organizationId, rateCardId, item.id)),
			]);

			const failedCount = results.filter((result) => result.status === "rejected").length;
			const persistedAny = failedCount < changeCount;

			if (failedCount === 0) {
				toast.success("Rate card saved");
			} else if (persistedAny) {
				toast.error(`${failedCount} of ${changeCount} change${changeCount === 1 ? "" : "s"} failed to save`);
			} else {
				// Nothing persisted, so local drafts are not stale — keep them so the user can retry
				// without duplicating items or losing their edits.
				toast.error("Failed to save rate card");
				return;
			}

			// At least one change persisted: reconcile local state with the server so a retry
			// doesn't recreate already-created items or reapply already-applied mutations.
			onSaved?.();
			await loadCard();
		} catch (error) {
			console.error("Error saving rate card:", error);
			const message = error instanceof Error ? error.message : "Failed to save rate card";
			toast.error(message);
			await loadCard();
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
			<div className="flex max-h-[90vh] w-full max-w-[90vw] flex-col rounded-lg bg-white shadow-xl">
				<div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
					<div>
						<h3 className="text-lg font-semibold text-gray-900">{card?.name ?? "Rate Card"}</h3>
						{card && <p className="mt-1 text-xs text-gray-500">Created {formatDate(card.createTime)}</p>}
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
						aria-label="Close"
					>
						<svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<div className="flex-1 overflow-y-auto px-6 py-4">
					{isLoading ? (
						<div className="flex justify-center py-12">
							<div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600" />
						</div>
					) : (
						<>
							<div className="mb-2 flex items-center justify-between">
								<h4 className="text-base font-semibold text-gray-900">Line Items</h4>
								<span className="text-xs text-gray-500">
									{items.length} item{items.length === 1 ? "" : "s"}
								</span>
							</div>
							<RateItemsReviewTable
								items={items}
								onChange={handleItemChange}
								onRemove={handleItemRemove}
								onAddRow={handleAddRow}
							/>
							{items.length > 0 && !allItemsValid && (
								<p className="mt-2 text-sm text-red-600">
									Every row needs a description, unit, and a non-negative unit price.
								</p>
							)}
						</>
					)}
				</div>

				<div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
					>
						Close
					</button>
					<button
						type="button"
						onClick={handleSave}
						disabled={!canSave}
						className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
					>
						{isSaving ? "Saving..." : "Save Changes"}
					</button>
				</div>
			</div>
		</div>
	);
}

export default RateCardDetailModal;
